import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  CreateAction,
  CreateFailureAction,
  CreateSuccessAction,
  DeleteAction,
  DeleteFailureAction,
  DeleteSuccessAction,
  InviteActionTypes,
  ResendEmailAction,
  ResendEmailFailureAction,
  ResendEmailSuccessAction,
  SaveFailureAction,
  SaveSuccessAction,
  SearchAction,
  SearchFailureAction,
  SearchSuccessAction,
} from './invite.actions';
import * as fromRoot from '../../redux/index';
import { InviteService } from '../../services/invite.service';

@Injectable()
export class InviteEffects {
  private store = inject<Store<fromRoot.State>>(Store);
  private actions$ = inject(Actions);
  private inviteService = inject(InviteService);

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteActionTypes.SearchAction),
      map((action: SearchAction) => action.payload),
      switchMap(params =>
        this.inviteService.findAll(params).pipe(
          map(invites => new SearchSuccessAction(invites)),
          catchError(errors => of(new SearchFailureAction(errors)))
        )
      )
    )
  );

  resendEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteActionTypes.ResendEmailAction),
      map((action: ResendEmailAction) => action.payload),
      switchMap((id: string) =>
        this.inviteService.resend(id).pipe(
          map(_ => new ResendEmailSuccessAction()),
          catchError(errors => of(new ResendEmailFailureAction(errors)))
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteActionTypes.CreateAction),
      map((action: CreateAction) => action.payload),
      switchMap(params =>
        this.inviteService.create(params).pipe(
          map(invite => new CreateSuccessAction(invite)),
          catchError(errors => of(new CreateFailureAction(errors)))
        )
      )
    )
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteActionTypes.SaveAction),
      map((action: CreateAction) => action.payload),
      switchMap(params =>
        this.inviteService.save(params).pipe(
          map(invite => new SaveSuccessAction(invite)),
          catchError(errors => of(new SaveFailureAction(errors)))
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteActionTypes.DeleteAction),
      map((action: DeleteAction) => action.payload),
      switchMap(id =>
        this.inviteService.delete(id).pipe(
          map(invite => new DeleteSuccessAction(invite)),
          catchError(errors => of(new DeleteFailureAction(errors)))
        )
      )
    )
  );
}
